import { useEffect, useRef } from "react";

/**
 * True on a component's first render, false on every one after.
 *
 * Reanimated plays an `entering` animation whenever a view mounts — including
 * the first paint of a screen. A transition authored to move *between* two
 * views therefore also ran on arrival at the screen and on refresh, which
 * reads as the content sliding in from nowhere. Gating `entering` on this
 * keeps the transition for later swaps and drops it for that first mount.
 *
 * A ref rather than state, deliberately: the value only has to be correct by
 * the time a *later* mount reads it during render, so there is nothing to
 * re-render for. Using state here would cause a second render on every
 * consumer for no visible change.
 */
export function useIsFirstRender(): boolean {
  const isFirstRender = useRef(true);

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  return isFirstRender.current;
}
